import { useEffect, useState } from 'react';
import { Bell, Check, ShieldMinus, ShieldPlus, Trash2, UserPlus, X } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import { useAuthStore } from '@/stores/AuthState';
import {
  useGroups,
  useGroupMembers,
  useJoinRequests,
  useManageGroup,
} from '@/hooks/queries/groups';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { toast } from '@/stores/toast';
import CircleEmojiField from '@/components/CircleEmojiField';
import { BackButton, PrimaryButton } from '@/components/Buttons';
import ConfirmSheet from '@/components/ConfirmSheet';
import PullToRefreshSpinner from '@/components/PullToRefreshSpinner';
import GroupNotifPrefsSheet from '@/components/GroupNotifPrefsSheet';
import InviteLinkSheet from '@/components/InviteLinkSheet';

/**
 * Manage circle screen (wireframe 3e): edit name/emoji, member list with
 * removal, leave, and delete. Rendered as a full-screen overlay.
 */
export default function ManageCircleScreen({
  groupId,
  onClose,
}: {
  groupId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const meId = useAuthStore((s) => s.userInfo.id);
  const { groups, refetch: refetchGroups } = useGroups();
  const group = groups.find((g) => g.id === groupId) ?? null;

  const {
    data: membersData,
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useGroupMembers(groupId);
  const members = membersData?.data ?? [];
  const { update, remove, leave, removeMember, promote, demote, approve, reject } =
    useManageGroup(groupId);

  const isAdmin = group?._membership?.role === 'admin';
  const canLeave = group?._membership?.canLeave ?? true;

  const { data: requests = [], refetch: refetchRequests } = useJoinRequests(groupId, isAdmin);

  // D1 — pull to refresh the whole screen: the circle itself (name, emoji and
  // my own role, which another admin may have just changed), the member list and
  // the pending join requests.
  const { pullY, refreshing, handlers } = usePullToRefresh(() =>
    Promise.all([refetchGroups(), refetchMembers(), refetchRequests()]),
  );

  const [name, setName] = useState(group?.name ?? '');
  const [emoji, setEmoji] = useState<string | null>(group?.emoji ?? null);
  const [confirm, setConfirm] = useState<
    null | 'leave' | 'delete' | 'revokeSelf' | { removeUserId: number }
  >(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [notifPrefsOpen, setNotifPrefsOpen] = useState(false);

  useEffect(() => {
    if (group) {
      setName(group.name);
      setEmoji(group.emoji);
    }
  }, [group?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = group && (name.trim() !== group.name || emoji !== group.emoji);

  const save = async () => {
    if (!name.trim()) return;
    try {
      await update.mutateAsync({ name: name.trim(), emoji });
      toast.success(t('groups.manage.saved'));
    } catch {
      toast.error(t('groups.manage.saveError'));
    }
  };

  const doDemote = (userId: number) => {
    demote.mutate(userId, { onError: () => toast.error(t('groups.manage.lastAdmin')) });
  };

  const doLeave = async () => {
    try {
      await leave.mutateAsync();
      toast.success(t('groups.manage.leftSuccess'));
      setConfirm(null);
      onClose();
    } catch {
      toast.error(t('groups.manage.actionError'));
    }
  };

  const doDelete = async () => {
    try {
      await remove.mutateAsync();
      toast.success(t('groups.manage.deletedSuccess'));
      setConfirm(null);
      onClose();
    } catch {
      toast.error(t('groups.manage.actionError'));
    }
  };

  const doRemoveMember = async (userId: number) => {
    try {
      await removeMember.mutateAsync(userId);
      setConfirm(null);
    } catch {
      toast.error(t('groups.manage.actionError'));
    }
  };

  return (
    <div
      className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:flex-col tw:overflow-y-auto tw:app-background tw:px-5 tw:pb-10"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}
      data-testid="manage-circle-screen"
      {...handlers}
    >
      <PullToRefreshSpinner pullY={pullY} refreshing={refreshing} />

      {/* Header */}
      <div className="tw:mb-6 tw:flex tw:items-center tw:justify-between">
        {/* D9 — the back button keeps its size; a long circle name scrolls instead. */}
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3">
          <BackButton onClick={onClose} data-testid="btn-manage-back" />
          <h1
            data-testid="manage-circle-title"
            className="tw:min-w-0 tw:flex-1 tw:overflow-x-auto tw:whitespace-nowrap tw:heading-3 tw:text-white"
          >
            {group?.name ?? t('groups.manage.title')}
          </h1>
        </div>
        <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
          {/* A6 — per-group notification preferences (every member, not admin-only). */}
          <button
            type="button"
            aria-label={t('groups.notifications.title')}
            data-testid="btn-manage-notif-prefs"
            onClick={() => setNotifPrefsOpen(true)}
            className="tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:btn-circle tw:text-white"
          >
            <Bell size={18} />
          </button>
          {isAdmin && (
            <button
              type="button"
              aria-label={t('groups.manage.invite')}
              data-testid="btn-manage-invite"
              onClick={() => setInviteOpen(true)}
              className="tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:btn-circle tw:text-white"
            >
              <UserPlus size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Emoji */}
      <div className="tw:mb-5">
        <CircleEmojiField value={emoji} onChange={setEmoji} disabled={!isAdmin} />
      </div>

      {/* Name */}
      <label className="tw:mb-2 tw:text-[13px] tw:font-semibold tw:text-grey-2">
        {t('groups.create.nameLabel')}
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={!isAdmin}
        maxLength={40}
        data-testid="input-manage-name"
        className="tw:mb-4 tw:h-12 tw:w-full tw:rounded-2xl tw:border tw:border-white/10 tw:bg-dark-1 tw:px-4 tw:text-[15px] tw:text-white tw:disabled:opacity-60"
      />

      {isAdmin && dirty && (
        <div className="tw:mb-6">
          <PrimaryButton
            label={t('groups.manage.save')}
            onClick={save}
            loading={update.isPending}
            data-testid="btn-manage-save"
          />
        </div>
      )}

      {/* Join requests (admin) */}
      {isAdmin && requests.length > 0 && (
        <div className="tw:mb-6" data-testid="manage-requests">
          <h2 className="tw:mb-3 tw:heading-3 tw:text-white">
            {t('groups.manage.requests')}
            <span className="tw:ml-2 tw:rounded-full tw:bg-blue-primary tw:px-2 tw:py-0.5 tw:text-[12px] tw:font-semibold tw:text-white">
              {requests.length}
            </span>
          </h2>
          <ul className="tw:flex tw:flex-col tw:gap-2">
            {requests.map((r) => (
              <li
                key={r.id}
                className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-1 tw:px-3 tw:py-2.5"
              >
                {r.user?.avatar_url ? (
                  <img
                    src={r.user.avatar_url}
                    alt=""
                    className="tw:size-9 tw:rounded-full tw:object-cover"
                  />
                ) : (
                  <span className="tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-[12px] tw:font-semibold tw:text-white">
                    {(r.user?.username ?? '?').slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="tw:flex-1 tw:truncate tw:text-[15px] tw:text-white">
                  {r.user?.username}
                </span>
                <button
                  type="button"
                  aria-label={t('groups.manage.approve')}
                  onClick={() => r.user && approve.mutate(r.user.id)}
                  data-testid={`btn-approve-${r.user?.id}`}
                  className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-green-3 tw:text-white"
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  aria-label={t('groups.manage.reject')}
                  onClick={() => r.user && reject.mutate(r.user.id)}
                  data-testid={`btn-reject-${r.user?.id}`}
                  className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-error tw:text-white"
                >
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Members */}
      <h2 className="tw:mb-3 tw:heading-3 tw:text-white">{t('groups.manage.members')}</h2>
      {membersLoading ? (
        <div className="tw:flex tw:justify-center tw:py-6">
          <span className="tw:size-6 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
        </div>
      ) : (
        <ul className="tw:flex tw:flex-col tw:gap-2">
          {members.map((m) => {
            const isMe = m.user?.id === meId;
            const removable = isAdmin && !isMe && m.canLeave;
            return (
              <li
                key={m.id}
                className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-1 tw:px-3 tw:py-2.5"
              >
                {m.user?.avatar_url ? (
                  <img
                    src={m.user.avatar_url}
                    alt=""
                    className="tw:size-9 tw:rounded-full tw:object-cover"
                  />
                ) : (
                  <span className="tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-[12px] tw:font-semibold tw:text-white">
                    {(m.user?.username ?? '?').slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="tw:flex-1 tw:truncate tw:text-[15px] tw:text-white">
                  {m.user?.username}
                  {m.role === 'admin' && (
                    <span className="tw:ml-2 tw:rounded-full tw:bg-blue-primary/25 tw:px-2 tw:py-0.5 tw:text-[11px] tw:text-blue-primary">
                      {t('groups.manage.admin')}
                    </span>
                  )}
                </span>
                {/* D10 — revoke admin, member stays; BE blocks removing the last admin. */}
                {isAdmin && m.role === 'admin' && (
                  <button
                    type="button"
                    aria-label={t('groups.manage.revokeAdmin')}
                    onClick={() => {
                      if (!m.user) return;
                      // Stepping down is not undoable by the user alone — ask first.
                      if (isMe) {
                        setConfirm('revokeSelf');
                        return;
                      }
                      doDemote(m.user.id);
                    }}
                    data-testid={`btn-demote-${m.user?.id}`}
                    className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-dark-1 tw:text-blue-primary"
                  >
                    <ShieldMinus size={16} />
                  </button>
                )}
                {isAdmin && !isMe && m.role !== 'admin' && (
                  <button
                    type="button"
                    aria-label={t('groups.manage.makeAdmin')}
                    onClick={() =>
                      m.user &&
                      promote.mutate(m.user.id, {
                        onError: () => toast.error(t('groups.manage.adminLimit')),
                      })
                    }
                    data-testid={`btn-promote-${m.user?.id}`}
                    className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-white"
                  >
                    <ShieldPlus size={16} />
                  </button>
                )}
                {removable && (
                  <button
                    type="button"
                    aria-label={t('groups.manage.remove')}
                    onClick={() => setConfirm({ removeUserId: m.user!.id })}
                    data-testid={`btn-remove-member-${m.user?.id}`}
                    className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-error tw:text-white"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Danger actions */}
      <div className="tw:mt-8 tw:flex tw:flex-col tw:items-center tw:gap-5">
        {canLeave && (
          <button
            type="button"
            onClick={() => setConfirm('leave')}
            data-testid="btn-leave-circle"
            className="tw:text-[15px] tw:font-semibold tw:text-error"
          >
            {t('groups.manage.leave')}
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setConfirm('delete')}
            data-testid="btn-delete-circle"
            className="tw:text-[15px] tw:font-semibold tw:text-error"
          >
            {t('groups.manage.delete')}
          </button>
        )}
      </div>

      {group && (
        <InviteLinkSheet
          open={inviteOpen}
          groupId={groupId}
          groupName={group.name}
          onClose={() => setInviteOpen(false)}
        />
      )}

      <GroupNotifPrefsSheet
        open={notifPrefsOpen}
        groupId={groupId}
        onClose={() => setNotifPrefsOpen(false)}
      />

      <ConfirmSheet
        open={confirm === 'leave'}
        title={t('groups.manage.leaveConfirm')}
        cancelLabel={t('groups.manage.cancel')}
        confirmLabel={t('groups.manage.leave')}
        loading={leave.isPending}
        onConfirm={doLeave}
        onClose={() => setConfirm(null)}
      />
      <ConfirmSheet
        open={confirm === 'delete'}
        title={t('groups.manage.deleteConfirm')}
        cancelLabel={t('groups.manage.cancel')}
        confirmLabel={t('groups.manage.delete')}
        loading={remove.isPending}
        onConfirm={doDelete}
        onClose={() => setConfirm(null)}
      />
      {/* Self-demotion is a one-way door for the tapping admin — confirm it. */}
      <ConfirmSheet
        open={confirm === 'revokeSelf'}
        title={t('groups.manage.revokeSelfConfirm')}
        cancelLabel={t('groups.manage.cancel')}
        confirmLabel={t('groups.manage.revokeAdmin')}
        loading={demote.isPending}
        // MR-2.1.0 — this used to call `doDemote` (fire-and-forget) and close
        // the sheet on the very next line, so `loading` was dead code and a
        // rejected self-demotion (you are the last admin) surfaced as a toast
        // over an already-dismissed sheet. Awaiting keeps both buttons disabled
        // for the round-trip and closes only once the call has settled — the
        // same contract as the three sibling sheets.
        onConfirm={async () => {
          if (!meId) return;
          try {
            await demote.mutateAsync(meId);
          } catch {
            toast.error(t('groups.manage.lastAdmin'));
          } finally {
            setConfirm(null);
          }
        }}
        onClose={() => setConfirm(null)}
      />
      <ConfirmSheet
        open={typeof confirm === 'object' && confirm !== null}
        title={t('groups.manage.removeConfirm')}
        cancelLabel={t('groups.manage.cancel')}
        confirmLabel={t('groups.manage.remove')}
        loading={removeMember.isPending}
        onConfirm={() => {
          if (typeof confirm === 'object' && confirm !== null) doRemoveMember(confirm.removeUserId);
        }}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
